import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import { AuthProvider } from "./context/AuthContext";

// load Tailwind + brand styles (lives in src/index.css)
import "./index.css";

// Initialize i18n for translations
import "./i18n";

// ============================================================================
// GLOBAL 401 INTERCEPTOR - Redirect to login on session expiry
// ============================================================================
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const response = await originalFetch(...args);

  // Check for 401 Unauthorized on API calls
  if (response.status === 401) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

    // Only handle 401 for API calls (not for external resources)
    if (url.includes('/api') || url.includes('/auth') || url.includes('/clients') ||
        url.includes('/comms') || url.includes('/phone-blast') || url.includes('/commissions') ||
        url.includes('/dashboard') || url.includes('/metrics') || url.includes('/tasks')) {

      // Don't redirect if already on login page
      if (!window.location.pathname.includes('/login') &&
          !window.location.pathname.includes('/signup') &&
          !window.location.pathname.includes('/reset')) {
        console.warn('[Fetch Interceptor] 401 detected, redirecting to login');
        window.location.href = '/login?expired=1';
      }
    }
  }

  return response;
};

// ============================================================================
// BLOCK NATIVE PWA INSTALL PROMPT ON PUBLIC PAGES
// The browser's beforeinstallprompt fires on ALL pages. We block it globally
// and only show our custom InstallPrompt component inside the protected app.
// This prevents the install prompt from appearing on login, surveys, etc.
// ============================================================================
let deferredInstallPrompt = null;

window.addEventListener('beforeinstallprompt', (e) => {
  // Always prevent the native mini-infobar
  e.preventDefault();

  // Store the event for later use by InstallPrompt component
  deferredInstallPrompt = e;

  // Dispatch custom event so InstallPrompt can pick it up (only if user is logged in)
  window.dispatchEvent(new CustomEvent('pwa-install-available', { detail: e }));
});

// Export for InstallPrompt component to use
window.getDeferredInstallPrompt = () => deferredInstallPrompt;
window.clearDeferredInstallPrompt = () => { deferredInstallPrompt = null; };

const el = document.getElementById("root");
createRoot(el).render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);
